/**
 * ConflictWarningBanner (EXACT copy from IntelligenceStudioPage.jsx lines 119-209)
 */

import React, { useState } from 'react';

import { AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';

// ==========================================
// ⚠️ ENTERPRISE FEATURE: CONFLICT WARNING BANNER
// ==========================================

const ConflictWarningBanner = ({ conflicts, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!conflicts || conflicts.length === 0) return null;

  const criticalCount = conflicts.filter(
    c => c.severity === 'critical' || c.severity === 'high'
  ).length;
  const visibleConflicts = isExpanded ? conflicts : conflicts.slice(0, 2);
  const hiddenCount = conflicts.length - 2;

  return (
    <div className="mx-4 mt-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-300 dark:border-amber-700">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {conflicts.length} Conflict{conflicts.length > 1 ? 's' : ''} Detected
            </span>
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded">
                {criticalCount} Critical
              </span>
            )}
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
            Different documents contain contradicting information. Review sources carefully.
          </p>
          <div className="space-y-1.5">
            {visibleConflicts.map((conflict, i) => (
              <div
                key={i}
                className="text-xs text-amber-600 dark:text-amber-500 flex items-start gap-1.5 p-2 bg-amber-50/50 dark:bg-amber-900/20 rounded-lg"
              >
                <span className="text-amber-400 mt-0.5">•</span>
                <div className="flex-1">
                  <div className="font-semibold text-amber-700 dark:text-amber-300 mb-0.5">
                    {conflict.topic || `Conflict ${i + 1}`}
                  </div>
                  <div className="text-amber-600 dark:text-amber-400">
                    <span className="font-medium">Doc 1:</span>{' '}
                    {conflict.doc1Claim?.slice(0, 100) || 'N/A'}...
                  </div>
                  <div className="text-amber-600 dark:text-amber-400 mt-0.5">
                    <span className="font-medium">Doc 2:</span>{' '}
                    {conflict.doc2Claim?.slice(0, 100) || 'N/A'}...
                  </div>
                  {conflict.doc1 && conflict.doc2 && (
                    <div className="text-[10px] text-amber-500 dark:text-amber-500 mt-1 flex gap-2">
                      <span>📄 {conflict.doc1}</span>
                      <span>vs</span>
                      <span>📄 {conflict.doc2}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ✅ FIXED: Expand/Collapse button that actually works */}
          {hiddenCount > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />+ {hiddenCount} more conflict
                  {hiddenCount > 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConflictWarningBanner;
